<template>
  <section>
    <nav class="level">
      <div class="level-left">
        <div class="level-item">
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="onFilterStatus">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.enabled') }}</option>
              <option value="false">{{ $t('attributes.disabled') }}</option>
            </b-select>
          </b-field>
        </div>

        <div class="level-item">
          <b-field>
            <b-input
              v-model="searchName"
              :placeholder="$t('attributes.search') + $t('attributes.name')"
              type="search"
              icon="magnify"
              @input="onFilterStatus"
            ></b-input>
          </b-field>
        </div>
      </div>

      <div class="level-right">
        <div class="level-item">
          <button class="button field is-primary" @click="onAdd">
            <b-icon icon="plus"></b-icon>
            <span>
              {{ $t('attributes.add') + $t('attributes.permission_group') }}
            </span>
          </button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="isLoading"
      hoverable
      narrowed
      bordered
      :mobile-cards="false"
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        :label="$t('attributes.id')"
        width="60"
        numeric
        sortable
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="status"
        :label="$t('attributes.activation_status')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <b-tag
          :type="props.row.status ? 'is-info' : 'is-danger'"
          size="is-medium"
        >
          {{ activeStatus(props.row.status) }}
        </b-tag>
      </b-table-column>

      <b-table-column
        field="title"
        :label="$t('attributes.name')"
        width="160"
        sortable
        v-slot="props"
      >
        {{ props.row.title }}
      </b-table-column>

      <b-table-column
        field="comment"
        :label="$t('attributes.comment')"
        width="160"
        v-slot="props"
      >
        {{ props.row.comment }}
      </b-table-column>

      <b-table-column
        field="employees"
        :label="$t('attributes.employee')"
        v-slot="props"
      >
        <b-field grouped group-multiline>
          <div
            v-for="e in props.row.employees"
            :key="e.emp_code"
            class="control"
          >
            <b-taglist attached>
              <b-tag type="is-info" size="is-small">{{ e.emp_code }}</b-tag>
              <b-tag v-if="e.status" type="is-success" size="is-small">
                {{ e.name }}
              </b-tag>
              <b-tag v-else type="is-dark" size="is-small">
                {{ e.name }}
              </b-tag>
            </b-taglist>
          </div>
        </b-field>
      </b-table-column>

      <b-table-column
        custom-key="actions"
        :width="232"
        label="功能"
        centered
        v-slot="props"
      >
        <div class="buttons">
          <b-button type="is-primary" @click.stop.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
          <b-button
            type="is-warning"
            @click.stop.prevent="onSetting(props.row)"
          >
            {{ $t('attributes.permission') }}
          </b-button>
          <b-button
            type="is-success"
            @click.stop.prevent="onChangeEmployees(props.row)"
          >
            {{ $t('attributes.employee') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isInfoFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <permission-group-form
        :permission-group="editItem"
        :sending.sync="sending"
        @send="onSend"
      />
    </b-modal>

    <b-modal
      :active.sync="isPermissionsFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <group-permissions-form
        :id="editItemId"
        :sending.sync="sending"
        @send="onUpdatePermissions"
      />
    </b-modal>

    <b-modal
      :active.sync="isEmployeesFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <group-employees-form
        :permission-group="editItem"
        :sending.sync="sending"
        @send="onUpdateEmployees"
      />
    </b-modal>
  </section>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { PERMISSION_GROUPS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import PermissionGroupForm from './PermissionGroupForm'
import GroupPermissionsForm from './GroupPermissionsForm'
import GroupEmployeesForm from './GroupEmployeesForm'

const { mapActions } = createNamespacedHelpers('app')

export default {
  name: 'PermissionGroupList',
  components: {
    PermissionGroupForm,
    GroupPermissionsForm,
    GroupEmployeesForm
  },

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadingItems: false,
      total: 0,
      items: [],

      filterStatus: this.query.status || '',
      searchName: this.query.search || '',
      sortField: this.query.sort || 'id',
      sortOrder: this.query.order || 'asc',
      defaultSortOrder: 'asc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      isInfoFormModalActive: false,
      isPermissionsFormModalActive: false,
      isEmployeesFormModalActive: false,
      editItem: null,
      editItemId: null,
      sending: false
    }
  },
  watch: {
    query(newQuery) {
      this.getQuery(newQuery)
      this.loadItems()
    }
  },
  computed: {
    isLoading() {
      return this.loadingItems
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    ...mapActions(['fetchPermissions']),
    async loadItems() {
      try {
        const api = `${PERMISSION_GROUPS_API}`
        const params = {
          active: this.filterStatus && this.filterStatus === 'true',
          search: this.searchName,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items.map(t => ({
          ...t,
          employees: t.employees
            .sort(e => e.emp_code)
            .sort((e1, e2) => e2.status - e1.status)
        }))
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('permission_group', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.filterStatus = query.status || ''
      this.searchName = query.search || ''
      this.sortField = query.sort || 'id'
      this.sortOrder = query.order || 'asc'
      this.page = query.page || 1
      this.perPage = query.page_size || 10
    },
    updateQuery() {
      const query = {
        status: this.filterStatus,
        search: this.searchName,
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilterStatus() {
      this.updateQuery()
    },
    onPageChange(page) {
      this.page = page

      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order

      this.updateQuery()
    },
    onAdd() {
      this.editItem = {
        id: null,
        status: false,
        title: '',
        comment: ''
      }
      this.isInfoFormModalActive = true
    },
    onModify(item) {
      this.editItem = item
      this.isInfoFormModalActive = true
    },
    onSetting(item) {
      this.editItem = item
      this.editItemId = item && item.id
      this.isPermissionsFormModalActive = true
    },
    onChangeEmployees(item) {
      this.editItem = item
      this.editItemId = item && item.id
      this.isEmployeesFormModalActive = true
    },
    onSend(item) {
      if (item.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    onUpdatePermissions(id, data) {
      this.updatePermissions(id, data)
    },
    onUpdateEmployees(id, data) {
      this.updateEmployees(id, data)
    },
    async createItem(item) {
      try {
        const api = `${PERMISSION_GROUPS_API}`

        this.sending = true
        await this.axios.post(api, item)

        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems()
        this.isInfoFormModalActive = false
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${PERMISSION_GROUPS_API}/${item.id}`

        this.sending = true
        const res = await this.axios.put(api, item)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.id === _item.id)
        const items = [...this.items]
        items[idx] = {
          ..._item,
          employees: _item.employees
            .sort(e => e.emp_code)
            .sort((e1, e2) => e2.status - e1.status)
        }
        this.items = items

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async updatePermissions(id, data) {
      try {
        const api = `${PERMISSION_GROUPS_API}/${id}/permissions`

        this.sending = true
        const res = await this.axios.put(api, data)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.id === _item.id)
        const items = [...this.items]
        items[idx] = {
          ..._item,
          employees: _item.employees
            .sort(e => e.emp_code)
            .sort((e1, e2) => e2.status - e1.status)
        }
        this.items = items

        notifyService.success(this.$t('attributes.update_succeed'))
        await this.fetchPermissions()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateEmployees(id, data) {
      try {
        const api = `${PERMISSION_GROUPS_API}/${id}/employees`

        this.sending = true
        const res = await this.axios.put(api, data)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.id === _item.id)
        const items = [...this.items]
        items[idx] = {
          ..._item,
          employees: _item.employees
            .sort(e => e.emp_code)
            .sort((e1, e2) => e2.status - e1.status)
        }
        this.items = items

        notifyService.success(this.$t('attributes.update_succeed'))
        await this.fetchPermissions()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
